import React, { useContext, useState, useEffect } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from 'react-router-dom';
import {
    Avatar,
    AppBar,
    Badge,
    Box,
    Button,
    Icon,
    IconButton,
    InputBase,
    Toolbar,
    Tooltip,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import {
    HelpOutline,
    Notifications
} from '@mui/icons-material';

import MenuIconOvererlay from './MenuIconOverlay';

import UserContext from '../../contexts/UserContext';
import ProjectContext from '../../contexts/ProjectContext';

import DateSelect from '../SideItems/DateSelect';

import KonsolLogo from '../logo/KonsolLogo';

import LinkSummaryDialog from '../dialog/LinkSummaryDialog';

import AccountContent from '../popover/content/AccountContent';
import NotificationContent from '../popover/content/NotificationContent';
import NotificationPopover from '../popover/NotificationPopover';


const Search = styled('div')(({ theme }) => ({
position: 'relative',
borderRadius: theme.shape.borderRadius,
backgroundColor: alpha(theme.palette.common.white, 0.15),
'&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
},
marginRight: theme.spacing(2),
marginLeft: 0,
width: '100%',
[theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: '400px',
},
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
padding: theme.spacing(0, 2),
height: '100%',
position: 'absolute',
pointerEvents: 'none',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
color: 'inherit',
'& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
    width: '330px',
    },
},
}));
const styles = {
    fadedAppBar: {
        transition: 'background-color 0.3s ease-in-out, box-shadow 0.2s ease-in-out',
        backgroundColor: (theme) => `rgba(${theme.palette.primary.main}, 0.7)`,
      },
    appBar  : {
        transition: 'background-color 0.3s ease-in-out, box-shadow 0.2s ease-in-out',
        background: 'transparent',
        boxShadow: 'none',
      },
};

export default function PrimarySearchAppBar(props) {
    const { 
        handleToggle, 
        avatar, 
        transferdate, 
        setTransferdate, 
        transferDates, 
        startTour, isFaded, 
        setIsFaded, isLocked, 
        isLoading, 
        highlighted, 
        linkSummary, 
        getLinkSummary,
        getTransferDates,
        websocketRef
    } = props 

    const { getAccessTokenSilently, isAuthenticated } = useAuth0();

    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('sm'))
    const isSm = useMediaQuery(theme.breakpoints.down('md'))
    const isMd = useMediaQuery(theme.breakpoints.down('lg'))
    const isLg = useMediaQuery(theme.breakpoints.down('xl'))
    
    const { konsolUser } = useContext(UserContext)
    const navigate = useNavigate()
    const projectContext = useContext(ProjectContext)

    const [allNotifications, setAllNotifications] = useState([])

    useEffect(() => {
        notificationsWebsocket()
    }, [isAuthenticated])

    const handleButtonClick = () => {
        setIsFaded(!isFaded);
        handleToggle(true)
    };

    const [accountOpen, setAccountOpen] = React.useState(false)
    const [anchorEl, setAnchorEl] = useState(null);
    const [notificationOpen, setNotificationOpen] = React.useState(false)
    const [newNotificationCount, setNewNotificationCount] = useState(0)
    const [accountAnchorEl, setAccountAnchorEl] = useState(null);
    const [linkSummaryOpen, setLinkSummaryOpen] = useState(false)

    useEffect(() => {
        setNewNotificationCount(allNotifications.filter(item => !item?.status?.hasOwnProperty(konsolUser?.sub) || item?.status[konsolUser?.sub] > 0).length)
    }, [allNotifications])

    const handleAccountOpen = (event) => {
        setAccountOpen(true)
        setAnchorEl(event.currentTarget);
    }
    const handleAccountClose = () => {
        setAccountOpen(false)
        setAnchorEl(null);
    };
    const handleNotificationOpen = (event) => {
        setNotificationOpen(true)
        setAnchorEl(event.currentTarget);
    }
    const handleNotificationClose = () => {
        setNotificationOpen(false)
        setAnchorEl(null);
    }
    const aboutPage = () => {
        navigate('/about')
    }


    async function notificationsWebsocket() {
        // Given this is the sole use of websockets in our app,
        //  we will get the token within this file itself multiple times.
        // If we use websockets elsewhere in the app in the future
        //  TODO: create a reusable websocket function like useFetch.jsx that
        //  gets/refreshes our tokens for us
        let accessToken = null
        if (isAuthenticated){
            accessToken = getAccessTokenSilently()
        } else {
            return
        }

        // Check if websocket already open, if yes, close it and then open a new one
        //   This usually triggers when user project switches using the side bar
        if (websocketRef.current && websocketRef.current.readyState == WebSocket.OPEN){
            await websocketRef.current.close()
        }
        const websocket = new WebSocket(process.env.REACT_APP_NOTIFICATION_WEBSOCKET)
        // Store our websocket in websocketRef
        websocketRef.current = websocket
        // Initial task on websocket open - send introduction message to auth and get initial notifications
        websocket.onopen = () => {
            websocket.send(JSON.stringify({
                "token": accessToken,
                "db_name": projectContext.project_db,
                "since": "000"
            }))
            // Listen for new notifications
            notificationListener()
        }
    }



    // Listen for new notifications relevant to this user (determined by Relay)
    function notificationListener(){
        websocketRef?.current.addEventListener('message', function(event){
            let result = event.data
            result = JSON.parse(result)
            setAllNotifications(prev => prev.concat(result))
        })

    }

    // Update a notification on the db via websockets
    function updateNotification(action, data){
        let accessToken = null
        if (isAuthenticated){
            accessToken = getAccessTokenSilently()
        } else {
            return
        }
        websocketRef?.current.send(JSON.stringify({"action": action, "data": data, "token": accessToken}))
    }

    function stringToColor(string) {
        let hash = 0;
        let i;
        for (i = 0; i < string.length; i += 1) {
          hash = string.charCodeAt(i) + ((hash << 5) - hash);
        }
        let color = '#';
        for (i = 0; i < 3; i += 1) {
          const value = (hash >> (i * 8)) & 0xff;
          color += `00${value.toString(16)}`.slice(-2);
        }
        return color;
    }

    return (
        <AppBar 
            color="primary"
            style={
                isFaded ? styles.appBar : styles.fadedAppBar
            }
            position="static">
                <Toolbar>
                <IconButton
                    id="appbar-sidebar-button"
                    size="large"
                    edge="start"
                    color="inherit"
                    aria-label="open drawer"
                    sx={{ mr: 1 }}
                    onClick={handleButtonClick}>
                    <MenuIconOvererlay isLocked={isLocked} highlighted={highlighted} />
                </IconButton>
                <Button 
                    id="appbar-konsol-logo"
                    disabled={transferDates.length === 0 && transferdate === ''}
                    onClick={aboutPage}
                    sx={{position: isXs ? 'relative' : undefined, left: isXs ? -20 : undefined}}
                    >
                    <KonsolLogo isLoading={isLoading} />
                </Button>
                <Box sx={{ width: '140px' }} />
                <Box sx={{ flexGrow: 1 }} />
                {/* <AdvancedSearch /> */}
                <Box sx={{ flexGrow: 1 }} />
                <Box sx={{  width:'400px', display: 'flex', justifyContent: 'flex-end' }}>
                    <Box 
                        id="appbar-transferdate-select"
                        sx={{
                            maxHeight: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                        <DateSelect
                            getLinkSummary={getLinkSummary}
                            linkSummary={linkSummary}
                            setLinkSummaryOpen={setLinkSummaryOpen}
                            setTransferdate={setTransferdate}
                            transferdate={transferdate}
                            transferDates={transferDates}
                        />
                    </Box>
                    <Box
                        display='flex'
                        alignItems='center'
                    >
                        <Badge
                            badgeContent={newNotificationCount}
                            variant='dot'
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right'
                            }}
                            sx={{
                                '& .MuiBadge-dot': {
                                    backgroundColor: 'red',
                                    marginTop: '10px'
                                },
                            }}
                        >
                            <IconButton
                                id="appbar-notification-icon"
                                edge="end"
                                color="inherit"
                                aria-label="notifications for the user"
                                aria-controls={'primary-search-notification-menu'}
                                aria-haspopup="true"
                                onClick={handleNotificationOpen}
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                    backgroundColor: 'rgba(255,255,255,0.08)',
                                    color: 'inherit',
                                    mr: 1,
                                    '&:hover': {
                                      backgroundColor: 'rgba(255,255,255,0.18)',
                                    },
                                  }}
                            >
                                <Notifications />
                            </IconButton>
                        </Badge>
                    </Box>
                    <IconButton
                        id="appbar-production-image"
                        size="large"
                        edge="end"
                        aria-haspopup="true"
                        color="inherit">
                        <Avatar
                            src={projectContext?.project_image}
                            children={
                                <img
                                    alt="Konsol"
                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                    src='/logo/konsol-k-icon.svg'
                                />
                            }
                            sx={{
                                width: 35,
                                height: 35,
                                backgroundColor: 'transparent',
                                border: '2px solid #274333',
                            }}
                        />
                    </IconButton>
                    <IconButton
                        color="inherit"
                        // sx={{ ml: 2 }}
                        onClick={() => window.FreshworksWidget && window.FreshworksWidget('open')}
                        aria-label="Support"
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255,255,255,0.08)',
                            color: 'inherit',
                            mr: 1,
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,0.18)',
                            },
                          }}
                    >
                        <HelpOutline />
                    </IconButton>
                    <IconButton
                        id="appbar-account-icon"
                        size="large"
                        edge="end"
                        aria-label="account of current user"
                        aria-controls={'primary-search-account-menu'}
                        aria-haspopup="true"
                        onClick={handleAccountOpen}
                        color="inherit">
                        <Avatar 
                        sx={{   bgcolor: stringToColor(konsolUser?.name ?? ''),
                            border: '2px solid #274333',
                            fontSize: '15px',
                            width: 35,
                            height: 35
                        }}
                    >
                        {`${(konsolUser?.name?.[0] ?? 'X').toUpperCase()}${(konsolUser?.name?.[1] ?? 'X').toUpperCase()}`}
                    </Avatar>
                    </IconButton>
                </Box>
            </Toolbar>
            <NotificationPopover 
                anchorEl={anchorEl} 
                open={accountOpen} 
                handleClose={handleAccountClose}
                content={<AccountContent avatar={avatar} handleClose={handleAccountClose} startTour={startTour} />}
            />
            <NotificationPopover 
                anchorEl={anchorEl} 
                open={notificationOpen} 
                handleClose={handleNotificationClose}
                content={<NotificationContent allNotifications={allNotifications} setAllNotifications={setAllNotifications} updateNotification={updateNotification} />}
            />
            <LinkSummaryDialog
                getLinkSummary={getLinkSummary}
                linkSummary={linkSummary}
                open={linkSummaryOpen}
                setOpen={setLinkSummaryOpen}
                transferDate={transferdate}
                getTransferDates={getTransferDates}
            />
        </AppBar>
    );
}